import { TextField } from "@mui/material";
import dayjs from "dayjs";
import {
  type DateRange,
  type DayEventHandler,
  type DayPickerProps,
  type Matcher,
} from "react-day-picker";
import { useIntl } from "react-intl";

import { CalendarBase } from "./CalendarBase";

type FieldInfo = {
  path: string;
  value: Date | undefined;
  label?: string;
};

export type CalendarProps = {
  startField: FieldInfo;
  endField?: FieldInfo;
  setFieldValue: (path: string, value: Date | undefined) => void;
  dropdown?: boolean;
  disabledDates?: Matcher;
  startMonth?: Date;
  endMonth?: Date;
  numberOfMonths?: number;
  allowSameDayRangeSelection?: boolean;
  selectionInfoVariant?: CalendarSelectionInfoProps["variant"];
};

export function Calendar({
  startField,
  endField,
  setFieldValue,
  dropdown,
  disabledDates,
  startMonth,
  endMonth,
  numberOfMonths,
  allowSameDayRangeSelection,
  selectionInfoVariant,
}: CalendarProps) {
  const selectedDay: Date | undefined = startField.value;

  const selectedRange: DateRange = {
    from: startField.value,
    to: endField?.value,
  };

  const handleDayClick: DayEventHandler<React.MouseEvent> = (date) => {
    // single date selection
    if (!endField) {
      setFieldValue(startField.path, date);
      return;
    }

    // date range selection
    if (selectedRange.to) {
      // whole range was already selected, begin to select new range
      setFieldValue(startField.path, date);
      setFieldValue(endField.path, undefined);
      return;
    }

    // make sure dates in range are in proper order
    if (dayjs(date).isBefore(selectedRange.from)) {
      setFieldValue(startField.path, date);
      setFieldValue(endField.path, selectedRange.from);
      return;
    }

    // same from and to dates selected
    if (!allowSameDayRangeSelection && dayjs(date).isSame(selectedRange.from)) {
      return;
    }

    setFieldValue(endField.path, date);
  };

  const fromValue = endField?.value ? selectedRange.from : selectedDay;

  const pickerProps = {
    captionLayout: dropdown ? "dropdown" : "label",
    disabled: disabledDates,
    defaultMonth: selectedDay || new Date(),
    required: true,
    startMonth,
    endMonth,
    numberOfMonths: numberOfMonths || 1,
    onSelect: () => {}, // needed to keep the calendar in controlled mode
    onDayClick: handleDayClick,
  } satisfies Partial<DayPickerProps>;

  return (
    <div
      className="flex min-h-0 shrink grow basis-auto flex-col items-center justify-start"
      data-sentry-block
    >
      <CalendarSelectionInfo
        variant={selectionInfoVariant}
        selectedRange={{
          from: fromValue,
          to: selectedRange.to,
        }}
        startField={startField}
        endField={endField}
      />

      {endField ? (
        <CalendarBase {...pickerProps} mode="range" selected={selectedRange} />
      ) : (
        <CalendarBase {...pickerProps} mode="single" selected={selectedDay} />
      )}
    </div>
  );
}

type CalendarSelectionInfoProps = {
  variant?: "inputs" | "text";
  selectedRange: DateRange;
  startField: FieldInfo;
  endField?: FieldInfo;
};

function CalendarSelectionInfo({
  variant = "inputs",
  selectedRange,
  startField,
  endField,
}: CalendarSelectionInfoProps) {
  const intl = useIntl();

  if (variant === "text") {
    if (!selectedRange.from) return null;

    const fromFormatted = dayjs(selectedRange.from).format("ddd D MMM");

    let content: string;
    if (selectedRange.to) {
      const toFormatted = dayjs(selectedRange.to).format("ddd D MMM");
      const nights = dayjs(selectedRange.to).diff(
        dayjs(selectedRange.from),
        "day",
      );

      content = `${fromFormatted} - ${toFormatted} (${intl.formatMessage({ id: "night", defaultMessage: "{count, plural, one {# night} other {# nights}}" }, { count: nights })})`;
    } else {
      content = `${fromFormatted} - ${intl.formatMessage({ id: "specifyTheDate", defaultMessage: "Specify the date" })}`;
    }

    return (
      <p className="mb-6 text-sm font-medium text-text-title">{content}</p>
    );
  }

  return (
    <div className="mb-8 flex w-full gap-2">
      <TextField
        value={
          selectedRange.from
            ? dayjs(selectedRange.from).format("ddd, D MMM")
            : ""
        }
        className="pointer-events-none w-[50%]"
        label={startField.label}
        slotProps={{
          input: {
            readOnly: true,
          },
          inputLabel: {
            shrink: true,
          },
        }}
      />
      {endField ? (
        <TextField
          value={
            selectedRange.to ? dayjs(selectedRange.to).format("ddd, D MMM") : ""
          }
          className="pointer-events-none w-[50%]"
          label={endField.label}
          slotProps={{
            input: {
              readOnly: true,
            },
            inputLabel: {
              shrink: true,
            },
          }}
        />
      ) : (
        <div className="w-[50%]" />
      )}
    </div>
  );
}
